import { auth } from '../firebase'; // Import Firebase auth instance
import { terminology } from '../config/terminology'; // Import terminology

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of clients (formerly households) from the backend.
 * @param {object} [options={}] - Optional parameters for filtering and pagination.
 * @param {string} [options.clientId] - Filter by Supplier (original Client) ID.
 * @param {number} [options.pageSize=10] - The number of clients to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @returns {Promise<object>} - The response data containing clients and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listHouseholds = async (options = {}) => { // Keep original name for now, update call sites first
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    clientId = null, // This is the Supplier ID
    pageSize = 10,
    startAfterDocId = null,
    status = null, // Status filter
  } = options;

  try {
    const idToken = await user.getIdToken();
    // API endpoint likely remains /household/list until backend changes
    const url = `${BASE_URL}/household/list`;
    const body = { pageSize };
    if (clientId) {
      body.clientId = clientId; // Pass Supplier ID
    }
    if (startAfterDocId) {
      body.startAfterDocId = startAfterDocId;
    }
    if (status) {
      body.status = status; // Pass status filter
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        // Update error message
        throw new Error(responseData.message || `Failed to list ${terminology.household.plural.toLowerCase()}`); // Use terminology
    }
    // The backend likely still returns a 'households' array
    return responseData.data; // Return { households: [], nextPageDocId: '...' }

  } catch (error) {
    // Update error message
    console.error(`Error listing ${terminology.household.plural.toLowerCase()}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Adds a new client (formerly household) via the backend API.
 * @param {object} clientData - The data for the new client. Must include clientId (Supplier ID).
 * @returns {Promise<object>} - The response data containing the newly added client.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addHousehold = async (clientData) => { // Keep original name for now
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  // clientId here refers to the Supplier ID
  if (!clientData.clientId) {
      // Update error message
      throw new Error(`${terminology.client.singular} ID (clientId) is required to add a ${terminology.household.singular.toLowerCase()}.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    // API endpoint likely remains /household/add
    const url = `${BASE_URL}/household/add`;

    // Remove empty optional fields before sending
    // The 'country' field from clientData will be included here if present
    const payload = { ...clientData };
    Object.keys(payload).forEach(key => {
        // Keep clientId (Supplier ID)
        if (key !== 'clientId' && (payload[key] === null || payload[key] === '')) {
            delete payload[key];
        }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        // Update error message
        throw new Error(responseData.message || `Failed to add ${terminology.household.singular.toLowerCase()}`); // Use terminology
    }

    return responseData.data;

  } catch (error) {
    // Update error message
    console.error(`Error adding ${terminology.household.singular.toLowerCase()}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Searches for clients (formerly households) based on a search string and optionally a Supplier ID.
 * @param {object} options - Search options.
 * @param {string} options.searchString - The string to search for (name, address, etc.).
 * @param {string} [options.clientId] - Optional Supplier (original Client) ID to scope the search.
 * @param {number} [options.pageSize=50] - Number of results to return.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after (for pagination).
 * @returns {Promise<object>} - The response data containing matching clients { households: [], nextPageDocId: null }.
 * @throws {Error} - Throws an error if the search fails or the user is not authenticated.
 */
export const searchHouseholds = async (options = {}) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated.");
    }
    
    const {
        searchString = '',
        clientId = null,
        pageSize = 50,
        startAfterDocId = null,
        status = null // Status filter
    } = options;
    
    if (!searchString || searchString.length < 3) {
        // Return consistent structure
        return { households: [], nextPageDocId: null };
    }

    try {
        const idToken = await user.getIdToken();
        // API endpoint likely remains /household/search
        const url = `${BASE_URL}/household/search`;

        const payload = {
            searchString: searchString,
            userType: "admin",
            pageSize: pageSize,
        };
        
        // Add startAfterDocId if provided
        if (startAfterDocId) {
            payload.startAfterDocId = startAfterDocId;
        }
        
        // clientId here is the Supplier ID
        if (clientId) {
            payload.clientId = clientId;
        }
        
        // Add status filter if provided
        if (status) {
            payload.status = status;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }
        if (responseData.status !== 'success') {
            // Update error message
            throw new Error(responseData.message || `Failed to search ${terminology.household.plural.toLowerCase()}`); // Use terminology
        }

        // Ensure consistent return structure, assuming backend returns { households: [...] }
        // Add nextPageDocId: null for now, update if backend supports pagination for search
        return {
            households: responseData.data?.households || [],
            nextPageDocId: responseData.data?.nextPageDocId || null // Adapt if backend provides this
        };

    } catch (error) {
        // Update error message
        console.error(`Error searching ${terminology.household.plural.toLowerCase()}:`, error); // Use terminology
        // Return consistent structure on error
        return { households: [], nextPageDocId: null, error: error.message };
    }
};

/**
 * Fetches details for a specific client (formerly household) by ID.
 * @param {string} householdId - The ID of the client to fetch.
 * @returns {Promise<object>} - The client data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getHouseholdById = async (householdId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!householdId) {
    throw new Error(`${terminology.household.singular} ID is required.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/household/get-by-id`;
    const payload = { householdId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to fetch ${terminology.household.singular.toLowerCase()} details`); // Use terminology
    }

    return responseData.data; // Return the client data object

  } catch (error) {
    console.error(`Error fetching ${terminology.household.singular.toLowerCase()} ${householdId}:`, error); // Use terminology
    throw error;
  }
};

/**
 * Updates an existing client (formerly household) via the backend API.
 * Supplier (clientId) and Country cannot be updated.
 * @param {string} householdId - The ID of the client to update.
 * @param {object} updatedData - An object containing the fields to update.
 * @returns {Promise<object>} - The response data containing the updated client's details.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const editHousehold = async (householdId, updatedData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!householdId) {
    throw new Error(`${terminology.household.singular} ID is required for editing.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/household/edit`;

    // Prepare payload, ensuring householdId is included
    const payload = {
      householdId,
      ...updatedData,
    };

    // Remove fields that shouldn't be sent (clientId, country, clientNameSearch, contactValidation)
    delete payload.country;
    delete payload.clientNameSearch; // Remove helper field if present
    delete payload.contactValidation; // Remove validation helper

    // Check if payload has any update data (other than householdId)
    const hasUpdateData = Object.keys(payload).length > 1;

    if (!hasUpdateData) {
      throw new Error('No update data provided.');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to update ${terminology.household.singular.toLowerCase()}`); // Use terminology
    }

    return responseData; // Return the full response (includes data and message)

  } catch (error) {
    console.error(`Error updating ${terminology.household.singular.toLowerCase()} ${householdId}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Deletes a client (formerly household) (marks as deleted) via the backend API.
 * @param {string} householdId - The ID of the client to delete.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const deleteHousehold = async (householdId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!householdId) {
    throw new Error(`${terminology.household.singular} ID is required for deletion.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/household/delete`;
    const payload = { householdId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to delete ${terminology.household.singular.toLowerCase()}`); // Use terminology
    }

    // Return success message or data if needed
    return responseData; // e.g., { status: 'success', message: '...' }

  } catch (error) {
    console.error(`Error deleting ${terminology.household.singular.toLowerCase()} ${householdId}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Get client information by household ID
 * @param {string} householdId - The household ID
 * @returns {Promise<object>} - The client data
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated
 */
export const getClientByHouseholdId = async (householdId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!householdId) {
    throw new Error("Household ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/household/get-client-by-household-id`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ householdId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.error || 'Failed to get client information');
    }

    return responseData.data; // Client data is in the 'data' property

  } catch (error) {
    console.error(`Error getting client for household ${householdId}:`, error);
    throw error;
  }
};